import type {
  ApprovalAction,
  ApprovalSummary,
  GiveBackAssessment,
  GiveBackUsage,
  GrantRefusal,
  WriteOffWhy,
} from '@didto/domain';
import {
  GIVE_BACK_WHY_LABEL,
  allowanceLeftToday,
  WRITE_OFF_HISTORY_LABEL,
  WRITE_OFF_LABEL,
  WRITE_OFF_WHY_LABEL,
  formatMinor,
  uuidv7,
} from '@didto/domain';
import type { AppError } from '@/lib/errors';
import { hintOf } from '@/lib/orders';
import type { Approval, ApprovalRequest } from './sheets';

/**
 * Who approves what at didto. (ADR-022). The cashier is the shift operator and sends before
 * payment, voids unserved lines and orders, and corrects serves directly, so these sheets appear
 * only for roles without those permissions. Reversing money (a payment void, a refund, voiding an
 * order that has payments) stays with the owner. The server lists only employees who hold the
 * approving permission; money and owner-level approvals take their 6-digit PIN (ADR-023).
 */
const COPY: Record<string, { title: string; reason: string }> = {
  'order.send_unpaid': {
    title: 'Cashier or owner approval',
    reason: 'This order is not paid yet. The cashier or the owner can approve sending it now.',
  },
  'order.void.approve': {
    title: 'Owner approval',
    reason: 'Voiding needs the owner’s approval here. The cashier can also void it directly.',
  },
  'refund.approve': {
    title: 'Owner approval',
    reason:
      'Payment voids and refunds stay with the owner: the owner checks what is shown and approves with their PIN.',
  },
  /* ADR-035: beyond the cashier's allowance (when the owner has it on) */
  'refund.approve:give_back': {
    title: 'Owner approval',
    reason:
      'This is beyond what a cashier gives back alone. The owner checks what is shown and approves with their PIN.',
  },
  /* ADR-024 */
  'shift.takeover.approve': {
    title: 'Owner approval',
    reason:
      'This shift is still in use. The owner can approve taking it over now; otherwise wait until it can be taken over without approval.',
  },
  /* ADR-031 */
  'writeoff.approve': {
    title: 'Owner approval',
    reason:
      'This write-off is beyond what a cashier writes off alone, or it is a comp. The owner checks what is shown and approves with their 6-digit PIN.',
  },
  /* ADR-032: a return shares the walk-outs' allowance */
  'writeoff.approve:return': {
    title: 'Owner approval',
    reason:
      'This return is beyond what a cashier returns alone. The owner checks what is shown and approves with their 6-digit PIN.',
  },
  'cash.receive': {
    title: 'Owner receives the cash',
    reason:
      'Without a safe, cash leaves the drawer only into the owner’s hands. The owner counts what they receive and confirms the amount with their PIN.',
  },
};

/** Why the server refused an approval the command carried (`hint.approval`, ADR-023). */
const GRANT_REFUSALS: Record<GrantRefusal, string> = {
  expired: 'The earlier approval expired before it was used.',
  used: 'The earlier approval was already used.',
  changed: 'The order changed after it was approved.',
  approver: 'The approver can no longer approve this.',
  mismatch: 'The earlier approval was for something else.',
  not_found: 'The earlier approval was not found.',
};

/** Leads with why an earlier approval was refused, when the refusal says so. */
export function withGrantRefusal(e: AppError, reason: string): string {
  const refusal = hintOf(e).approval as GrantRefusal | undefined;
  const note = refusal ? GRANT_REFUSALS[refusal] : undefined;
  return note ? `${note} Approve it again. ${reason}` : reason;
}

export function approvalCopy(permission: string, e: AppError): { title: string; reason: string } {
  const copy = COPY[permission];
  if (!copy) return { title: 'Approval', reason: withGrantRefusal(e, e.message) };
  if (permission === 'writeoff.approve') {
    // a write-off's refusal carries `write_off`, a return's `return` (ADR-032); both say why
    const h = hintOf(e);
    const isReturn = typeof h.return === 'object' && h.return !== null;
    const own = (isReturn ? COPY['writeoff.approve:return'] : undefined) ?? copy;
    const why =
      ((isReturn ? h.return : h.write_off) as { why?: WriteOffWhy[] } | undefined)?.why ?? [];
    const said = why.map((w) => WRITE_OFF_WHY_LABEL[w]).filter(Boolean);
    return {
      title: own.title,
      reason: withGrantRefusal(e, said.length ? `${said.join('. ')}. ${own.reason}` : own.reason),
    };
  }
  if (permission === 'refund.approve' && hintOf(e).give_back) {
    // ADR-035: the allowance is on and this is beyond it; the refusal says why
    const own = COPY['refund.approve:give_back'] ?? copy;
    const why = (hintOf(e).give_back as Partial<GiveBackAssessment>).why ?? [];
    const said = why.map((w) => GIVE_BACK_WHY_LABEL[w]).filter(Boolean);
    return {
      title: own.title,
      reason: withGrantRefusal(e, said.length ? `${said.join('. ')}. ${own.reason}` : own.reason),
    };
  }
  if (permission === 'refund.approve' && hintOf(e).reverses_minor !== undefined)
    return {
      title: copy.title,
      reason: withGrantRefusal(e, `Voiding this order reverses its payments. ${copy.reason}`),
    };
  return { title: copy.title, reason: withGrantRefusal(e, copy.reason) };
}

/**
 * A refused order void names what it needs: its own approval (`order.void.approve`) or, when the
 * order has payments and the actor cannot reverse them, the owner's (`refund.approve`).
 */
export function voidApprovalPermission(e: AppError): string {
  return hintOf(e).requires === 'refund.approve' ? 'refund.approve' : 'order.void.approve';
}

/** An approval of one exact command: its kind and the payload it will send (ADR-023). */
export function approvalRequest(
  kind: ApprovalAction,
  payload: Record<string, unknown>,
  copy: { title: string; reason: string },
  onApproved: (approval?: Approval) => Promise<void> | void,
): ApprovalRequest {
  return { requestId: uuidv7(), kind, payload, ...copy, onApproved };
}

/**
 * ADR-035: after money given back on the cashier's allowance (no approval), what is left of the cashier's day. Empty
 * for anything else.
 */
export function allowanceNote(
  result: Record<string, unknown>,
  givenMinor: number,
  currency: string,
): string {
  if (result.cashier_allowance !== true) return '';
  const usage = result.allowance as GiveBackUsage | null | undefined;
  if (!usage) return '; on your allowance';
  return `; on your allowance, ${formatMinor(allowanceLeftToday(usage, givenMinor), currency)} left today`;
}

/** ADR-035: why a refund or payment void beyond the cashier's allowance comes to the owner, and the day so far. */
function giveBackLines(
  g: GiveBackAssessment | undefined,
  money: (minor: number | undefined) => string,
): string[] {
  if (!g) return [];
  const lines: string[] = [];
  if (g.why.length > 0)
    lines.push(`Why the owner: ${g.why.map((w) => GIVE_BACK_WHY_LABEL[w]).join('; ')}`);
  lines.push(
    `Given back alone on this order: ${money(g.usage.order_given_minor)} of ${money(g.usage.order_max_minor)}; by this cashier today: ${money(g.usage.day_given_minor)} of ${money(g.usage.day_max_minor)}`,
  );
  return lines;
}

/** What the approver reads before entering their PIN, built from the server's record. */
export function approvalSummaryLines(s: ApprovalSummary): string[] {
  const money = (minor: number | undefined) => formatMinor(minor ?? 0, s.currency ?? 'PHP');
  const order = s.order_number ? `order ${s.order_number}` : 'this order';
  const lines: string[] = [];
  switch (s.action) {
    case 'order.send':
      lines.push(`Send ${order} before it is paid (${money(s.unpaid_minor)} unpaid)`);
      break;
    case 'order.void_line':
      lines.push(
        `Void ${s.qty ?? 1} × ${s.line ?? 'line'} (${money(s.line_total_minor)}) on ${order}`,
      );
      break;
    case 'order.void':
      lines.push(`Void ${order} (${money(s.total_minor)})`);
      if ((s.reverses_minor ?? 0) > 0) lines.push(`Gives back ${money(s.reverses_minor)}`);
      break;
    case 'payment.void':
      lines.push(
        `Void the ${s.payment_method ?? ''} payment on ${order}: ${money(s.reverses_minor)} back`,
      );
      lines.push(...giveBackLines(s.give_back, money));
      break;
    case 'order.refund':
      lines.push(`Refund ${money(s.amount_minor)} on ${order}`);
      lines.push(
        s.refund_method
          ? `Back by ${s.refund_method}`
          : s.payment_method
            ? `Back from the ${s.payment_method} payment`
            : 'Back the way it was paid',
      );
      if (s.lines && s.lines.length > 0)
        lines.push(`Items: ${s.lines.map((l) => `${l.qty} × ${l.name}`).join(', ')}`);
      lines.push(...giveBackLines(s.give_back, money));
      break;
    case 'ticket.unserve':
      lines.push(`Take back ticket ${s.ticket_seq ?? ''} on ${order}`);
      break;
    case 'order.unserve':
      lines.push(`Take back ${order}`);
      break;
    case 'shift.takeover_start':
      lines.push(
        `Take over ${s.shift_cashier ?? 'the cashier'}'s shift on ${s.drawer ?? 'the drawer'}`,
      );
      if (s.shift_status === 'closing') lines.push('Their count was started and not finished');
      break;
    case 'order.write_off':
      lines.push(
        `${WRITE_OFF_LABEL[s.kind ?? 'walkout']} on ${order}: ${money(s.amount_minor)} written off`,
      );
      if (s.kind === 'staff_meal' && s.employee) lines.push(`Eaten by ${s.employee}`);
      if (s.why && s.why.length > 0)
        lines.push(`Why the owner: ${s.why.map((w) => WRITE_OFF_WHY_LABEL[w]).join('; ')}`);
      if (s.history && s.history.length > 0)
        lines.push(`This order had ${s.history.map((h) => WRITE_OFF_HISTORY_LABEL[h]).join(', ')}`);
      break;
    case 'order.return_line':
      lines.push(
        `Return ${s.qty ?? 1} × ${s.line ?? 'item'} on ${order}: ${money(s.amount_minor)} off the order`,
      );
      if ((s.refund_minor ?? 0) > 0) lines.push(`Gives back ${money(s.refund_minor)}`);
      lines.push('Nothing goes back to stock or the kitchen');
      if (s.why && s.why.length > 0)
        lines.push(`Why the owner: ${s.why.map((w) => WRITE_OFF_WHY_LABEL[w]).join('; ')}`);
      if (s.history && s.history.length > 0)
        lines.push(`This order had ${s.history.map((h) => WRITE_OFF_HISTORY_LABEL[h]).join(', ')}`);
      break;
    case 'shift.close_finish':
      lines.push(`Receive ${money(s.removed_minor)} from ${s.drawer ?? 'the drawer'} now`);
      lines.push(
        `Counted ${money(s.counted_minor)}; ${money(s.retained_minor)} stays in the drawer`,
      );
      break;
  }
  if (s.reason) lines.push(`Reason: ${s.reason}`);
  if (s.requested_by) lines.push(`Asked by ${s.requested_by}`);
  return lines;
}
